import React, { useState, useEffect, useRef } from 'react';
import { BUILDING_OPTIONS, buildingForGrade, shouldUpdateBuildingOnGradeChange } from '../utils/schoolBuilding';
import { Routes, Route, Link, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import NotificationBell from './shared/NotificationBell';
import AccountSwitcher from './shared/AccountSwitcher';
import { getDistrictLogoCandidates, getDistrictLogoSrc } from '../utils/apiUrl';
import { getContrastStyles, getBestTextColor } from '../utils/colorContrast';
import { getFrInfoDocUrl } from '../config/districtDocs';
import NewStudentForm from './parent/NewStudentForm';
import EditStudentForm from './parent/EditStudentForm';
import ReturningStudentList from './parent/ReturningStudentList';
import ReturningStudentForm from './parent/ReturningStudentForm';
import ReturningStudentEnrollmentForm from './parent/ReturningStudentEnrollmentForm';
import MyStudentsList from './parent/MyStudentsList';
import StudentIntakeList from './parent/StudentIntakeList';
import StudentIntakeForm from './parent/StudentIntakeForm';
import EditStudent from './parent/EditStudent';
import ParentFormsDashboard from './parent/ParentFormsDashboard';
import HouseholdInformation from './parent/HouseholdInformation';
import RecentlyDeleted from './parent/RecentlyDeleted';
import GettingStarted from './parent/GettingStarted';
import MigrantQuestionnaireRoute from './parent/MigrantQuestionnaireRoute';
import SignatureField from './shared/SignatureField';
import './Dashboard.css';
import './SuperAdminDashboard.css';
import './ParentDashboard.css';

// Custom styles for the school lunch form
const schoolLunchStyles = `
  .school-lunch-form {
    max-width: 800px;
    margin: 0 auto;
  }

  .school-lunch-section {
    margin-bottom: 2rem;
    padding: 1.5rem;
    border-radius: 8px;
    border: 1px solid #e0e0e0;
  }

  .school-lunch-title {
    margin-top: 0;
    margin-bottom: 0.5rem;
    color: #2c3e50;
    font-size: 1.3rem;
    font-weight: 600;
  }

  .school-lunch-field {
    display: flex;
    flex-direction: column;
    margin-bottom: 1rem;
  }

  .school-lunch-label {
    margin-bottom: 0.5rem;
    font-weight: 500;
    color: #333;
  }

  .school-lunch-input,
  .school-lunch-select,
  .school-lunch-textarea {
    padding: 0.75rem;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 1rem;
    font-family: inherit;
  }

  .school-lunch-input:focus,
  .school-lunch-select:focus,
  .school-lunch-textarea:focus {
    outline: none;
    border-color: #3498db;
    box-shadow: 0 0 0 2px rgba(52, 152, 219, 0.2);
  }

  .school-lunch-checkbox-label {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    cursor: pointer;
    font-weight: normal;
  }

  .school-lunch-checkbox {
    margin: 0;
    cursor: pointer;
  }

  /* Instruction callout. The district reported parents were not listing every household
     member; the digital form had dropped the paper form's instructions entirely, so there was
     nothing to read, let alone anything to miss. Amber rather than red: this is guidance to
     follow, not an error to fix. */
  .school-lunch-callout {
    border-left: 4px solid #e8a33d;
    background-color: #fdf8ef;
    padding: 0.85rem 1rem;
    border-radius: 4px;
    margin-bottom: 1.25rem;
    font-size: 0.95rem;
    line-height: 1.55;
    color: #4a3c28;
  }

  .school-lunch-callout strong {
    color: #8a5a12;
  }

  .school-lunch-callout ul {
    margin: 0.5rem 0 0;
    padding-left: 1.25rem;
  }

  .school-lunch-callout li {
    margin-bottom: 0.25rem;
  }

  .school-lunch-child-section {
    margin-bottom: 15px;
    padding: 10px;
    background-color: #f9f9f9;
    border-radius: 3px;
  }

  .school-lunch-child-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 10px;
  }

  .school-lunch-child-title {
    margin: 0;
    font-size: 1.1rem;
    font-weight: 600;
  }

  .school-lunch-remove-btn {
    padding: 4px 8px;
    background-color: #dc3545;
    color: white;
    border: none;
    border-radius: 3px;
    cursor: pointer;
    font-size: 12px;
  }

  .school-lunch-add-btn {
    padding: 8px 16px;
    background-color: #007bff;
    color: white;
    border: none;
    border-radius: 3px;
    cursor: pointer;
    margin-top: 15px;
  }

  .school-lunch-submit-btn {
    padding: 12px 24px;
    background-color: #28a745;
    color: white;
    border: none;
    border-radius: 4px;
    font-size: 16px;
    cursor: pointer;
    align-self: flex-start;
  }

  .school-lunch-submit-btn:hover {
    background-color: #218838;
  }

  /* Success message styles */
  .success-message {
    text-align: center;
    padding: 2rem;
    background-color: #f8fff8;
    border-radius: 8px;
    border: 1px solid #d4edda;
  }

  .success-icon {
    font-size: 4rem;
    margin-bottom: 1rem;
  }

  .success-title {
    font-size: 1.5rem;
    color: #155724;
    margin-bottom: 1rem;
  }

  .success-text {
    font-size: 1.1rem;
    margin-bottom: 2rem;
    color: #155724;
  }

  .success-subtitle {
    margin-bottom: 2rem;
    color: #666;
    font-size: 1rem;
  }

  /* Signature error styling */
  .signature-error {
    color: #e74c3c;
    font-size: 0.875rem;
    margin-top: 0.5rem;
    font-weight: 500;
  }

  /* Signature field styles */
  .signature-field {
    margin-bottom: 1.5rem;
  }

  .signature-canvas-container {
    position: relative;
    display: inline-block;
    border: 2px solid #ddd;
    border-radius: 4px;
    background: white;
    cursor: crosshair;
  }

  .signature-canvas {
    display: block;
    width: 100%;
    max-width: 600px;
    height: 200px;
    touch-action: none;
  }

  .signature-canvas-container:hover {
    border-color: #667eea;
  }

  .signature-clear-btn {
    position: absolute;
    top: 8px;
    right: 8px;
    padding: 0.4rem 0.8rem;
    background: #dc3545;
    color: white;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-size: 0.85rem;
    z-index: 10;
  }

  .signature-clear-btn:hover {
    background: #c82333;
  }

  .signature-hint {
    margin-top: 0.5rem;
    font-size: 0.85rem;
    color: #666;
    font-style: italic;
  }
`;

// Inject the styles
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.textContent = schoolLunchStyles;
  document.head.appendChild(styleSheet);
}

// Working school lunch form component (no external dependencies)
const SchoolLunchApplicationForm = () => {
  const navigate = useNavigate();
  const [submissionSuccess, setSubmissionSuccess] = useState(false);
  const [signatureError, setSignatureError] = useState('');
  const [caseNumberError, setCaseNumberError] = useState('');
  // { total, listed } while the household-count confirmation is on screen; null otherwise.
  const [householdWarning, setHouseholdWarning] = useState(null);
  // ISO date of the application this form was prefilled from, or null if starting fresh.
  const [prefilledFrom, setPrefilledFrom] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  // Shown beside the submit button on failure. Replaces alert(), which a parent can dismiss and
  // then have no idea whether the application saved.
  const [submitError, setSubmitError] = useState('');
  /**
   * Synchronous re-entrancy guard.
   *
   * `submitting` state cannot do this job alone: React batches state updates, so two clicks
   * in the same tick both read submitting === false and both proceed. A ref updates
   * immediately, which is what actually stops a double-click.
   */
  const submitInFlight = useRef(false);
  const [formData, setFormData] = useState({
    children: [{ first_name: '', middle_initial: '', last_name: '', building_name: 'Elementary', grade: 'K', special_status: [] }],
    snap_tanf_fdpir: 'No',
    adult_members: [{
      name: '',
      earnings_amount: 0,
      earnings_frequency: '',
      public_assistance_amount: 0,
      public_assistance_frequency: '',
      other_income_amount: 0,
      other_income_frequency: ''
    }],
    // Starts empty, not 1. A pre-filled 1 that cannot be erased (see the onChange below)
    // concatenated with whatever the parent typed: a household of 5 was stored as 15.
    total_household_members: '',
    no_ssn: [],
    child_income_amount: 0,
    adult_signer_first_name: '',
    adult_signer_last_name: '',
    signature_date: '',
    signature: '',
    mailing_address_street: '',
    mailing_address_city: '',
    mailing_address_state: '',
    mailing_address_zip: '',
    phone: '',
    email: ''
  });

  /**
   * Prefill from the parent's most recent submitted application.
   *
   * The form is append-only: correcting an application means submitting a new one. Starting
   * blank meant a parent who left one household member off had to retype every child, adult
   * and income figure to add them - a strong reason not to bother, and a likely contributor
   * to the under-listing the district reported. Prefilling turns that into "add the person
   * you missed".
   *
   * The signature and its date are deliberately NOT carried over. They certify the content
   * they were given with; reusing them would attach an old certification to new answers.
   * Leaving them blank means the existing required-signature validation forces a fresh one,
   * and the parent states the date of the declaration they are actually making.
   */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Dedicated endpoint, not /progress?include_completed=true - that returns the 10 most
        // recent submissions across all form types, so a family with several children could
        // have their lunch application fall outside it and get no prefill at all.
        const res = await axios.get('/api/forms/school-lunch-application/my-latest');
        if (cancelled || !res.data) return;

        const prior = res.data;
        const data = { ...prior.submission_data };
        delete data._form_type;
        delete data._form_revision;
        delete data._language;

        setFormData((current) => ({
          ...current,
          ...data,
          // fresh declaration required - see the note above
          signature: '',
          signature_date: ''
        }));
        setPrefilledFrom(prior.submitted_at || null);
      } catch (e) {
        // Prefill is a convenience. If it fails the parent gets a blank form, which still works.
        console.error('Could not prefill from the previous application:', e);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const addChild = () => {
    setFormData({
      ...formData,
      children: [...formData.children, { first_name: '', middle_initial: '', last_name: '', building_name: 'Elementary', grade: 'K', special_status: [] }]
    });
  };

  const removeChild = (index) => {
    if (formData.children.length > 1) {
      const newChildren = formData.children.filter((_, i) => i !== index);
      setFormData({
        ...formData,
        children: newChildren
      });
    }
  };

  /**
   * How many people the parent has actually listed.
   *
   * Counts only rows with a name in them. The form starts with one blank child row and one
   * blank adult row, so counting array length would credit the parent with two people they
   * never entered - and the whole point of the check is to compare against a real count.
   */
  const countListedPeople = () => {
    const named = (v) => String(v || '').trim() !== '';
    const children = (formData.children || []).filter(
      (c) => named(c.first_name) || named(c.last_name)
    ).length;
    const adults = (formData.adult_members || []).filter((a) => named(a.name)).length;
    return children + adults;
  };

  /**
   * Everything that happens once the parent has cleared validation and any warning.
   * Split out from handleSubmit so the warning dialog's "I'm sure" can call it directly,
   * rather than re-running handleSubmit and re-triggering the same warning.
   */
  const submitApplication = async () => {
    // Each submission is a new row by design, so a double-click does not overwrite - it files
    // the application twice. Measured on real data: 14 duplicate pairs, the closest 43ms apart,
    // with byte-identical answers differing only in per-request signature metadata. The button
    // had no disabled state and no in-flight guard, so a double-click or an impatient second
    // click while the first request was still going produced two applications.
    if (submitInFlight.current) return;
    submitInFlight.current = true;
    setSubmitting(true);

    setHouseholdWarning(null);

    /**
     * Only released on FAILURE.
     *
     * On success the guard deliberately stays locked until the confirmation screen replaces the
     * form. Releasing it in a `finally` reopened the button for the moment between the response
     * arriving and the screen swapping - and a parent who is unsure whether it worked clicks
     * again in exactly that moment, which is how duplicates happen in the first place.
     */
    const release = () => {
      submitInFlight.current = false;
      setSubmitting(false);
    };

    try {
      const submitResponse = await axios.post('/api/forms/school-lunch-application/submit', {
        formData: formData
      });
      if (submitResponse.data.success) {
        setSignatureError('');
        window.dispatchEvent(new Event('progressUpdated'));
        setSubmissionSuccess(true); // replaces the form; guard stays locked
        return;
      }
      setSubmitError(submitResponse.data.message || 'The application was not saved. Please try again.');
      release();
    } catch (error) {
      setSubmitError(
        error.response?.data?.error
        || 'We could not save your application. Your answers are still here — please try again.'
      );
      release();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate signature
    if (!formData.signature || !formData.signature.image) {
      setSignatureError('Please sign the application before submitting.');
      // Scroll to signature field
      document.getElementById('signature-section')?.scrollIntoView({ behavior: 'smooth' });
      return;
    }

    // Clear any previous signature error
    setSignatureError('');

    // Validate case number when SNAP/TANF/FDPIR is Yes
    if (formData.snap_tanf_fdpir === 'Yes') {
      if (!formData.case_number || formData.case_number.trim() === '') {
        setCaseNumberError('Case Number is required when participating in SNAP, TANF, or FDPIR.');
        // Scroll to case number field
        document.getElementById('case-number-section')?.scrollIntoView({ behavior: 'smooth' });
        return;
      }
    }

    // Clear any previous case number error
    setCaseNumberError('');

    /**
     * Household count check - a WARNING, never a rejection.
     *
     * The district reported parents are not listing every household member. This catches the
     * common case: a stated household of 5 with only 2 people entered.
     *
     * Deliberately not blocking. A household can legitimately be larger than the names on the
     * form, and the parent is closer to the truth than we are - refusing their submission over
     * a mismatch we cannot adjudicate would turn a nudge into a dead end. They confirm and
     * proceed.
     */
    const listed = countListedPeople();
    const total = parseInt(formData.total_household_members, 10);
    if (Number.isFinite(total) && total !== listed) {
      setHouseholdWarning({ total, listed });
      return;
    }

    await submitApplication();
  };

  const handleOkClick = () => {
    navigate('/parent/forms');
  };

  if (submissionSuccess) {
    return (
      <div className="school-lunch-form">
        <h2 className="success-title">Application Submitted Successfully!</h2>
        <div className="success-message">
          <div className="success-icon">✅</div>
          <p className="success-text">
            Your school lunch application has been submitted successfully!
          </p>
          <p className="success-subtitle">
            District administrators can now view your application and generate the PDF when needed.
          </p>
          <p className="success-subtitle" style={{ marginTop: '0.75rem' }}>
            Need to submit an update for this year (for example, income changed)? You can file another application;
            your school will see the latest version for the current enrollment year.
          </p>
          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginTop: '1rem' }}>
            <button type="button" onClick={() => setSubmissionSuccess(false)} className="school-lunch-submit-btn">
              Submit an update
            </button>
            <button type="button" onClick={handleOkClick} className="school-lunch-submit-btn" style={{ background: '#6c757d' }}>
              OK
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="school-lunch-form">
      <h2>School Lunch Application</h2>
      <p>Complete this form to apply for free or reduced price school meals. Your application data will be submitted and stored for district administrators to review and generate PDFs. All fields are required unless marked optional.</p>

      {getFrInfoDocUrl() && (
        <div style={{
          background: '#eef5fb',
          border: '1px solid #cfe2f3',
          borderRadius: '4px',
          padding: '0.75rem 1rem',
          marginBottom: '1.5rem'
        }}>
          📄{' '}
          <a
            href={getFrInfoDocUrl()}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: '#2c6ca0', fontWeight: 600 }}
          >
            Free &amp; Reduced Price Meals Information (PDF)
          </a>
          {' '}— please review this information before completing your application.
        </div>
      )}

      {/*
        Household count confirmation. A warning, not a gate: "I'm sure" submits unchanged.

        Wording states both numbers so the parent can see which one they believe is wrong,
        rather than being told vaguely that something does not add up. "Go back" returns them
        to the form with everything still filled in - nothing is cleared.
      */}
      {householdWarning && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="household-warning-title"
          style={{
            position: 'fixed', inset: 0, zIndex: 1000,
            background: 'rgba(28, 40, 51, 0.45)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem'
          }}
        >
          <div style={{
            background: '#fff', borderRadius: '8px', maxWidth: '520px', width: '100%',
            padding: '1.5rem', boxShadow: '0 10px 40px rgba(0,0,0,0.25)'
          }}>
            <h3 id="household-warning-title" style={{ margin: '0 0 0.75rem', color: '#8a5a12', fontSize: '1.15rem' }}>
              Are you sure you added all household members and income?
            </h3>
            <p style={{ margin: '0 0 0.5rem', lineHeight: 1.55 }}>
              You entered <strong>{householdWarning.total}</strong>{' '}
              {householdWarning.total === 1 ? 'household member' : 'household members'}, but
              only listed <strong>{householdWarning.listed}</strong>{' '}
              {householdWarning.listed === 1 ? 'person' : 'people'} by name.
            </p>
            <p style={{ margin: '0 0 1.25rem', lineHeight: 1.55, color: '#5a6b7a', fontSize: '0.95rem' }}>
              Everyone in your household should be listed &mdash; including infants, children at
              other schools, children not applying for meals, and any adult who lives with you,
              even if they have no income.
            </p>
            <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
              <button
                type="button"
                onClick={() => setHouseholdWarning(null)}
                style={{
                  padding: '0.65rem 1.25rem', background: '#3498db', color: '#fff',
                  border: 'none', borderRadius: '4px', fontSize: '1rem', cursor: 'pointer'
                }}
              >
                Go back and check
              </button>
              <button
                type="button"
                onClick={submitApplication}
                disabled={submitting}
                style={{
                  padding: '0.65rem 1.25rem', background: '#fff', color: '#33475b',
                  border: '1px solid #c3ced8', borderRadius: '4px', fontSize: '1rem',
                  cursor: submitting ? 'not-allowed' : 'pointer',
                  opacity: submitting ? 0.65 : 1
                }}
              >
                {submitting ? 'Submitting…' : 'I\u2019m sure \u2014 submit'}
              </button>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="school-lunch-form">

        {/* Says plainly where the answers came from, so a prefilled form is never a surprise,
            and names the one thing the parent must redo. */}
        {prefilledFrom && (
          <div style={{
            borderLeft: '4px solid #3498db', background: '#f4f8fb',
            padding: '0.85rem 1rem', borderRadius: '4px', marginBottom: '1.5rem',
            fontSize: '0.95rem', lineHeight: 1.55, color: '#33475b'
          }}>
            <strong>We filled this in from your last application</strong>, submitted on{' '}
            {new Date(prefilledFrom).toLocaleDateString()}. Review it, make your changes, and{' '}
            <strong>sign again at the bottom</strong> &mdash; your signature has to match what
            you are submitting now. Your previous application stays on file; your school will
            use this newer one.
          </div>
        )}

        {/* Children Section */}
        <div className="school-lunch-section">
          <h3 className="school-lunch-title">Step 1: Children Information</h3>

          {/* Wording follows the official DESE form's Step 1 instructions, which the digital
              form previously omitted. The four easy-to-miss cases are called out separately
              because those are the ones parents leave off. */}
          <div className="school-lunch-callout">
            <strong>List EVERY child in your household</strong> &mdash; infants through grade 12.
            This includes children who:
            <ul>
              <li>attend a <strong>different school</strong>, or no school at all</li>
              <li>are <strong>not applying</strong> for free or reduced price meals</li>
              <li>are <strong>infants or toddlers</strong></li>
              <li>are <strong>not related to you</strong> but live in your household</li>
            </ul>
            Leaving a child off can change whether your household qualifies.
          </div>

          {formData.children.map((child, index) => (
            <div key={index} className="school-lunch-child-section">
              <div className="school-lunch-child-header">
                <h4 className="school-lunch-child-title">Child {index + 1}</h4>
                {formData.children.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeChild(index)}
                    className="school-lunch-remove-btn"
                  >
                    Remove
                  </button>
                )}
              </div>
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                <input
                  type="text"
                  className="school-lunch-input"
                  placeholder="First Name"
                  value={child.first_name}
                  onChange={(e) => {
                    const newChildren = [...formData.children];
                    newChildren[index].first_name = e.target.value;
                    setFormData({...formData, children: newChildren});
                  }}
                  required
                />
                <input
                  type="text"
                  className="school-lunch-input"
                  placeholder="MI"
                  value={child.middle_initial}
                  maxLength="1"
                  onChange={(e) => {
                    const newChildren = [...formData.children];
                    newChildren[index].middle_initial = e.target.value;
                    setFormData({...formData, children: newChildren});
                  }}
                />
                <input
                  type="text"
                  className="school-lunch-input"
                  placeholder="Last Name"
                  value={child.last_name}
                  onChange={(e) => {
                    const newChildren = [...formData.children];
                    newChildren[index].last_name = e.target.value;
                    setFormData({...formData, children: newChildren});
                  }}
                  required
                />
                {/*
                  Building. Two options, per Brandy: Elementary and High School, split at 7th
                  grade. Preschool is offered separately because that is how a preschool parent
                  thinks of it, and reports as Elementary because Lumen SIS files it there.

                  Junior high is deliberately absent - it exists as a concept in the district
                  but not as its own building. The grade stays on every record, so if that ever
                  changes the affected rows can be re-derived from grade instead of re-asked.

                  This was free text until now, and the result is why it is a select: of the
                  241 building answers on file across this form and the migrant questionnaire,
                  a third were unusable - "E", "C", "?", the district's own name - and 90% of
                  children had no building at all, leaving that DESE column blank.

                  Pre-filled from grade so nobody has to think about it, and still changeable
                  for a child placed outside the building their grade implies. The backend
                  re-derives it when printing, so a stale value here cannot reach the form.
                */}
                <select
                  className="school-lunch-select"
                  value={
                    // A legacy free-text value ("E", "Jr high") matches no option, so the
                    // select would render blank on a required field - which is what a parent
                    // editing one of the 9 submitted applications that carry one would see.
                    // Fall back to the grade-derived value so it always shows something
                    // sensible, and the same thing the form will print.
                    BUILDING_OPTIONS.some((o) => o.value === child.building_name)
                      ? child.building_name
                      : (buildingForGrade(child.grade) || '')
                  }
                  onChange={(e) => {
                    const newChildren = [...formData.children];
                    newChildren[index].building_name = e.target.value;
                    setFormData({...formData, children: newChildren});
                  }}
                  required
                >
                  <option value="">Building…</option>
                  {BUILDING_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
                <select
                  className="school-lunch-select"
                  value={child.grade}
                  onChange={(e) => {
                    const newChildren = [...formData.children];
                    const previousGrade = newChildren[index].grade;
                    newChildren[index].grade = e.target.value;
                    // Refresh the building only if it is empty or still shows what the OLD
                    // grade implied, i.e. nobody has touched it. A deliberate choice survives,
                    // which is the case worth protecting: a child placed outside the building
                    // their grade implies.
                    if (shouldUpdateBuildingOnGradeChange(newChildren[index].building_name, previousGrade)) {
                      newChildren[index].building_name = buildingForGrade(e.target.value) || '';
                    }
                    setFormData({...formData, children: newChildren});
                  }}
                  required
                >
                  <option value="Preschool">Preschool</option>
                  <option value="Daycare">Daycare</option>
                  <option value="K">Kindergarten</option>
                  <option value="1">1st Grade</option>
                  <option value="2">2nd Grade</option>
                  <option value="3">3rd Grade</option>
                  <option value="4">4th Grade</option>
                  <option value="5">5th Grade</option>
                  <option value="6">6th Grade</option>
                  <option value="7">7th Grade</option>
                  <option value="8">8th Grade</option>
                  <option value="9">9th Grade</option>
                  <option value="10">10th Grade</option>
                  <option value="11">11th Grade</option>
                  <option value="12">12th Grade</option>
                  <option value="Not in School">Not in School</option>
                </select>
              </div>

              {/* Foster / Homeless / Migrant / Runaway. These are on the official
                  application and drive eligibility and program referrals, so the
                  parent has to be able to report them here. */}
              <div style={{ marginTop: '0.75rem' }}>
                <span className="school-lunch-label" style={{ display: 'block', marginBottom: '0.35rem' }}>
                  Check all that apply (optional)
                </span>
                <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                  {['Foster Child', 'Homeless', 'Migrant', 'Runaway'].map((status) => (
                    <label key={status} className="school-lunch-checkbox-label">
                      <input
                        type="checkbox"
                        className="school-lunch-checkbox"
                        checked={(child.special_status || []).includes(status)}
                        onChange={(e) => {
                          const newChildren = [...formData.children];
                          const current = newChildren[index].special_status || [];
                          newChildren[index].special_status = e.target.checked
                            ? [...current, status]
                            : current.filter((s) => s !== status);
                          setFormData({ ...formData, children: newChildren });
                        }}
                      />
                      {status}
                    </label>
                  ))}
                </div>
              </div>
            </div>
          ))}

          <div style={{ marginTop: '15px' }}>
            <button
              type="button"
              onClick={addChild}
              className="school-lunch-add-btn"
            >
              + Add Another Child
            </button>
          </div>
        </div>

        {/* Household Income */}
        <div className="school-lunch-section">
          <h3 className="school-lunch-title">Step 2: Household Income</h3>

          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              Do any household members participate in SNAP, TANF, or FDPIR?
            </label>
            <div>
              <label style={{ marginRight: '15px' }}>
                <input
                  type="radio"
                  name="snap_tanf_fdpir"
                  value="Yes"
                  checked={formData.snap_tanf_fdpir === 'Yes'}
                  onChange={(e) => {
                    setFormData({...formData, snap_tanf_fdpir: e.target.value});
                    if (caseNumberError) setCaseNumberError('');
                  }}
                /> Yes
              </label>
              <label>
                <input
                  type="radio"
                  name="snap_tanf_fdpir"
                  value="No"
                  checked={formData.snap_tanf_fdpir === 'No'}
                  onChange={(e) => {
                    setFormData({...formData, snap_tanf_fdpir: e.target.value});
                    if (caseNumberError) setCaseNumberError('');
                  }}
                /> No
              </label>
            </div>
          </div>

          {formData.snap_tanf_fdpir === 'Yes' && (
            <div id="case-number-section" style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                Case Number
              </label>
              <input
                type="text"
                value={formData.case_number || ''}
                onChange={(e) => {
                  setFormData({...formData, case_number: e.target.value});
                  if (caseNumberError) setCaseNumberError('');
                }}
                style={{ padding: '8px', border: '1px solid #ccc', borderRadius: '3px', width: '200px' }}
                required
              />
              {caseNumberError && (
                <div className="case-number-error" style={{ color: 'red', fontSize: '14px', marginTop: '5px' }}>
                  {caseNumberError}
                </div>
              )}
            </div>
          )}

          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              Total Household Members (Children and Adults)
            </label>
            {/*
              `parseInt(x) || 1` made this field un-erasable: clearing it gave NaN, so the 1
              came straight back and the parent's digits landed after it ("1" + "5" = 15).
              Empty has to survive as empty. `required` and min="1" below keep a blank value
              from being submitted.

              Capped at TWO digits, because the DESE form prints this into two cells and a
              third digit cannot be shown at all. The cap is enforced here in the onChange
              rather than with maxLength, which browsers ignore on type="number". A household
              over 99 is not a real answer, and the printed form has nowhere to put it.
            */}
            <input
              type="number"
              value={formData.total_household_members}
              onChange={(e) => {
                // Strip to digits and keep at most two, so a stray keystroke or a paste
                // cannot produce a value the official form has no room for.
                const digits = String(e.target.value).replace(/\D/g, '').slice(0, 2);
                setFormData({
                  ...formData,
                  total_household_members: digits === '' ? '' : parseInt(digits, 10)
                });
              }}
              style={{ padding: '8px', border: '1px solid #ccc', borderRadius: '3px', width: '300px' }}
              min="1"
              max="99"
              required
            />
          </div>

          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              Child Income (Total for ALL children)
            </label>
            <input
              type="number"
              value={formData.child_income_amount}
              onChange={(e) => setFormData({...formData, child_income_amount: parseFloat(e.target.value) || 0})}
              style={{ padding: '8px', border: '1px solid #ccc', borderRadius: '3px', width: '250px' }}
              min="0"
              step="0.01"
            />
          </div>

          {formData.child_income_amount > 0 && (
            <div style={{ marginBottom: '15px' }}>
              <select
                value={formData.child_income_frequency || ''}
                onChange={(e) => setFormData({...formData, child_income_frequency: e.target.value})}
                style={{ padding: '8px', border: '1px solid #ccc', borderRadius: '3px', width: '200px' }}
                required
              >
                <option value="">How often?</option>
                <option value="Weekly">Weekly</option>
                <option value="Every 2 Weeks">Every 2 Weeks</option>
                <option value="2x Month">2x Month</option>
                <option value="Monthly">Monthly</option>
                <option value="Annual">Annual</option>
              </select>
            </div>
          )}

          {/* Adult Household Members */}
          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '10px', fontWeight: 'bold' }}>
              Adult Household Members (Income Information)
            </label>

            {/* Follows the official form's Step 3A wording. "Including yourself" and "even if
                they have no income" are the two most commonly missed, so both are bolded. */}
            <div className="school-lunch-callout">
              <strong>List EVERY adult who lives with you</strong> and shares income and
              expenses &mdash; <strong>including yourself</strong>, and{' '}
              <strong>even if they have no income</strong>. Include adults who are not related
              to you. If someone has no income, list them and enter 0.
            </div>

            {formData.adult_members.map((adult, index) => (
              <div key={index} style={{ marginBottom: '15px', padding: '10px', backgroundColor: '#f9f9f9', borderRadius: '3px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                  <h4 style={{ margin: 0 }}>Adult {index + 1}</h4>
                  {formData.adult_members.length > 1 && (
                    <button
                      type="button"
                      onClick={() => {
                        const newAdults = formData.adult_members.filter((_, i) => i !== index);
                        setFormData({...formData, adult_members: newAdults});
                      }}
                      style={{
                        padding: '4px 8px',
                        backgroundColor: '#dc3545',
                        color: 'white',
                        border: 'none',
                        borderRadius: '3px',
                        cursor: 'pointer',
                        fontSize: '12px'
                      }}
                    >
                      Remove
                    </button>
                  )}
                </div>

                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '10px' }}>
                  <div style={{ flex: 1, minWidth: '200px' }}>
                    <label style={{ display: 'block', marginBottom: '5px', fontSize: '0.9rem' }}>
                      Full Name
                    </label>
                    <input
                      type="text"
                      className="school-lunch-input"
                      value={adult.name}
                      onChange={(e) => {
                        const newAdults = [...formData.adult_members];
                        newAdults[index].name = e.target.value;
                        setFormData({...formData, adult_members: newAdults});
                      }}
                      placeholder="First and Last Name"
                    />
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '10px' }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', marginBottom: '5px', fontSize: '0.9rem' }}>
                      Earnings from Work
                    </label>
                    <input
                      type="number"
                      className="school-lunch-input"
                      value={adult.earnings_amount || ''}
                      onChange={(e) => {
                        const newAdults = [...formData.adult_members];
                        newAdults[index].earnings_amount = parseFloat(e.target.value) || 0;
                        setFormData({...formData, adult_members: newAdults});
                      }}
                      placeholder="0.00"
                      min="0"
                      step="0.01"
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', marginBottom: '5px', fontSize: '0.9rem' }}>
                      How Often?
                    </label>
                    <select
                      className="school-lunch-select"
                      value={adult.earnings_frequency || ''}
                      onChange={(e) => {
                        const newAdults = [...formData.adult_members];
                        newAdults[index].earnings_frequency = e.target.value;
                        setFormData({...formData, adult_members: newAdults});
                      }}
                    >
                      <option value="">Select frequency</option>
                      <option value="Weekly">Weekly</option>
                      <option value="Every 2 Weeks">Every 2 Weeks</option>
                      <option value="2x Month">2x Month</option>
                      <option value="Monthly">Monthly</option>
                      <option value="Annual">Annual</option>
                    </select>
                  </div>
                </div>

                {/* Public Assistance is a column on the DESE form that nothing used to ask
                    for, so it printed blank on every application. Sits between Earnings and
                    Other Income to match the order of the columns on the printed form. */}
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '10px' }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', marginBottom: '5px', fontSize: '0.9rem' }}>
                      Public Assistance, Child Support, Alimony
                    </label>
                    <input
                      type="number"
                      className="school-lunch-input"
                      value={adult.public_assistance_amount || ''}
                      onChange={(e) => {
                        const newAdults = [...formData.adult_members];
                        newAdults[index].public_assistance_amount = parseFloat(e.target.value) || 0;
                        setFormData({...formData, adult_members: newAdults});
                      }}
                      placeholder="0.00"
                      min="0"
                      step="0.01"
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', marginBottom: '5px', fontSize: '0.9rem' }}>
                      How Often?
                    </label>
                    <select
                      className="school-lunch-select"
                      value={adult.public_assistance_frequency || ''}
                      onChange={(e) => {
                        const newAdults = [...formData.adult_members];
                        newAdults[index].public_assistance_frequency = e.target.value;
                        setFormData({...formData, adult_members: newAdults});
                      }}
                    >
                      <option value="">Select frequency</option>
                      <option value="Weekly">Weekly</option>
                      <option value="Every 2 Weeks">Every 2 Weeks</option>
                      <option value="2x Month">2x Month</option>
                      <option value="Monthly">Monthly</option>
                    </select>
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', marginBottom: '5px', fontSize: '0.9rem' }}>
                      Other Income (Pensions, SSI, etc.)
                    </label>
                    <input
                      type="number"
                      className="school-lunch-input"
                      value={adult.other_income_amount || ''}
                      onChange={(e) => {
                        const newAdults = [...formData.adult_members];
                        newAdults[index].other_income_amount = parseFloat(e.target.value) || 0;
                        setFormData({...formData, adult_members: newAdults});
                      }}
                      placeholder="0.00"
                      min="0"
                      step="0.01"
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', marginBottom: '5px', fontSize: '0.9rem' }}>
                      How Often?
                    </label>
                    <select
                      className="school-lunch-select"
                      value={adult.other_income_frequency || ''}
                      onChange={(e) => {
                        const newAdults = [...formData.adult_members];
                        newAdults[index].other_income_frequency = e.target.value;
                        setFormData({...formData, adult_members: newAdults});
                      }}
                    >
                      {/* No "Annual" here: the DESE form's Other Income column set stops at
                          Monthly, so an Annual answer has no box to tick and prints as a bare
                          amount the district cannot annualize. Same for Public Assistance. */}
                      <option value="">Select frequency</option>
                      <option value="Weekly">Weekly</option>
                      <option value="Every 2 Weeks">Every 2 Weeks</option>
                      <option value="2x Month">2x Month</option>
                      <option value="Monthly">Monthly</option>
                    </select>
                  </div>
                </div>
              </div>
            ))}

            <button
              type="button"
              onClick={() => {
                if (formData.adult_members.length < 5) {
                  setFormData({
                    ...formData,
                    adult_members: [...formData.adult_members, {
                      name: '',
                      earnings_amount: 0,
                      earnings_frequency: '',
                      public_assistance_amount: 0,
                      public_assistance_frequency: '',
                      other_income_amount: 0,
                      other_income_frequency: ''
                    }]
                  });
                }
              }}
              className="school-lunch-add-btn"
              disabled={formData.adult_members.length >= 5}
            >
              + Add Another Adult Member
            </button>
          </div>
        </div>

        {/* Contact Information */}
        <div className="school-lunch-section">
          <h3 className="school-lunch-title">Step 3: Contact Information</h3>

          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              Adult Signer Name
            </label>
            <div style={{ display: 'flex', gap: '10px' }}>
              <input
                type="text"
                placeholder="First Name"
                value={formData.adult_signer_first_name}
                onChange={(e) => setFormData({...formData, adult_signer_first_name: e.target.value})}
                style={{ padding: '8px', border: '1px solid #ccc', borderRadius: '3px', flex: 1 }}
                required
              />
              <input
                type="text"
                placeholder="Last Name"
                value={formData.adult_signer_last_name}
                onChange={(e) => setFormData({...formData, adult_signer_last_name: e.target.value})}
                style={{ padding: '8px', border: '1px solid #ccc', borderRadius: '3px', flex: 1 }}
                required
              />
            </div>
          </div>

          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              Signature Date
            </label>
            <input
              type="date"
              value={formData.signature_date}
              onChange={(e) => setFormData({...formData, signature_date: e.target.value})}
              style={{ padding: '8px', border: '1px solid #ccc', borderRadius: '3px', width: '200px' }}
              required
            />
          </div>

          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              Mailing Address
            </label>
            <input
              type="text"
              placeholder="Street Address"
              value={formData.mailing_address_street}
              onChange={(e) => setFormData({...formData, mailing_address_street: e.target.value})}
              style={{ padding: '8px', border: '1px solid #ccc', borderRadius: '3px', width: '100%' }}
            />
          </div>

          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              City, State, ZIP
            </label>
            <div style={{ display: 'flex', gap: '10px' }}>
              <input
                type="text"
                placeholder="City"
                value={formData.mailing_address_city}
                onChange={(e) => setFormData({...formData, mailing_address_city: e.target.value})}
                style={{ padding: '8px', border: '1px solid #ccc', borderRadius: '3px', flex: 2 }}
              />
              <input
                type="text"
                placeholder="State"
                value={formData.mailing_address_state}
                onChange={(e) => setFormData({...formData, mailing_address_state: e.target.value})}
                style={{ padding: '8px', border: '1px solid #ccc', borderRadius: '3px', width: '80px' }}
              />
              <input
                type="text"
                placeholder="ZIP"
                value={formData.mailing_address_zip}
                onChange={(e) => setFormData({...formData, mailing_address_zip: e.target.value})}
                style={{ padding: '8px', border: '1px solid #ccc', borderRadius: '3px', width: '100px' }}
              />
            </div>
          </div>

          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              Contact Information
            </label>
            <div style={{ display: 'flex', gap: '10px' }}>
              <input
                type="text"
                placeholder="Phone"
                value={formData.phone}
                onChange={(e) => setFormData({...formData, phone: e.target.value})}
                style={{ padding: '8px', border: '1px solid #ccc', borderRadius: '3px', flex: 1 }}
              />
              <input
                type="email"
                placeholder="Email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                style={{ padding: '8px', border: '1px solid #ccc', borderRadius: '3px', flex: 1 }}
              />
            </div>
          </div>

          {/* Signature Section */}
          <div id="signature-section" style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              Parent/Guardian Signature *
            </label>
            <SignatureField
              value={formData.signature}
              onChange={(value) => {
                setFormData({...formData, signature: value});
                // Clear error when user starts signing
                if (signatureError) setSignatureError('');
              }}
              required={true}
              label="Parent/Guardian Signature"
              name="signature"
            />
            {signatureError && (
              <div className="signature-error">
                {signatureError}
              </div>
            )}
          </div>

          {/* SSN Section */}
          <div className="school-lunch-field">
            <label className="school-lunch-checkbox-label">
              <input
                type="checkbox"
                className="school-lunch-checkbox"
                checked={formData.no_ssn.includes('No Social Security Number')}
                onChange={(e) => setFormData({
                  ...formData,
                  no_ssn: e.target.checked ? ['No Social Security Number'] : []
                })}
              />
              Check if no Social Security Number
            </label>
          </div>

            {!formData.no_ssn.includes('No Social Security Number') && (
              <div className="school-lunch-field">
                <input
                  type="text"
                  className="school-lunch-input"
                  placeholder="Last 4 digits of SSN"
                  value={formData.ssn_last_four || ''}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, ''); // Only allow digits
                    setFormData({...formData, ssn_last_four: value});
                  }}
                  maxLength="4"
                  pattern="[0-9]{4}"
                  required
                />
              </div>
            )}
        </div>

        <button
          type="submit"
          className="school-lunch-submit-btn"
          disabled={submitting}
          style={submitting ? { opacity: 0.65, cursor: 'not-allowed' } : undefined}
        >
          {submitting ? 'Sending your application…' : 'Submit Application'}
        </button>

        {/* A disabled button on its own reads as stuck, and a parent who is not sure whether it
            worked clicks again - which is what produced duplicate applications. Saying what is
            happening, and that the page will change by itself, removes the reason to click. */}
        {submitting && (
          <p style={{
            margin: '0.6rem 0 0', fontSize: '0.95rem', color: '#5a6b7a', lineHeight: 1.5
          }}>
            Please wait &mdash; this can take a few seconds. Do not close this page or press
            the button again; it will change by itself when your application is saved.
          </p>
        )}

        {submitError && (
          <p style={{
            margin: '0.6rem 0 0', padding: '0.7rem 0.85rem', fontSize: '0.95rem',
            lineHeight: 1.5, color: '#8a1f1f', background: '#fdf3f3',
            border: '1px solid #f0c9c9', borderRadius: '4px'
          }}>
            {submitError} <strong>Nothing has been lost</strong> &mdash; your answers and
            signature are still on this page.
          </p>
        )}
      </form>
    </div>
  );
};

const ParentDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [incompleteForms, setIncompleteForms] = useState([]);
  // Students still needing the state-required migrant questionnaire. Families who
  // enrolled before this existed would otherwise never know to complete it, since the
  // incomplete-forms alert only tracks combined-form drafts.
  const [migrantOutstanding, setMigrantOutstanding] = useState([]);
  const [changeRequests, setChangeRequests] = useState([]);
  const [requiredForms, setRequiredForms] = useState([]);
  // Collapsed unless the parent opens it. Kept for the session so it does not reopen on
  // every navigation within the portal.
  const [incompleteAlertOpen, setIncompleteAlertOpen] = useState(() => {
    try { return sessionStorage.getItem('incomplete_alert_open') === '1'; } catch (e) { return false; }
  });
  useEffect(() => {
    try { sessionStorage.setItem('incomplete_alert_open', incompleteAlertOpen ? '1' : '0'); } catch (e) { /* private mode */ }
  }, [incompleteAlertOpen]);
  const [migrantAlertOpen, setMigrantAlertOpen] = useState(() => {
    try { return sessionStorage.getItem('migrant_alert_open') === '1'; } catch (e) { return false; }
  });
  useEffect(() => {
    try { sessionStorage.setItem('migrant_alert_open', migrantAlertOpen ? '1' : '0'); } catch (e) { /* private mode */ }
  }, [migrantAlertOpen]);
  const [requiredAlertOpen, setRequiredAlertOpen] = useState(() => {
    try { return sessionStorage.getItem('required_alert_open') === '1'; } catch (e) { return false; }
  });
  useEffect(() => {
    try { sessionStorage.setItem('required_alert_open', requiredAlertOpen ? '1' : '0'); } catch (e) { /* private mode */ }
  }, [requiredAlertOpen]);
  const [schoolLunchSubmission, setSchoolLunchSubmission] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Close mobile menu when route changes
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  // Redirect to getting-started or student-intake based on dismissal preference
  useEffect(() => {
    if (location.pathname === '/parent' || location.pathname === '/parent/') {
      const gettingStartedDismissed = localStorage.getItem('gettingStartedDismissed');
      if (gettingStartedDismissed === 'true') {
        navigate('/parent/student-intake', { replace: true });
      } else {
        navigate('/parent/getting-started', { replace: true });
      }
    }
  }, [location.pathname, navigate]);

  useEffect(() => {
    checkIncompleteForm();
    checkSchoolLunchSubmission();
  }, [user, location.pathname]); // Re-check when route changes

  // Re-check when navigating to dashboard home or when window gains focus
  useEffect(() => {
    const handleFocus = () => {
      checkIncompleteForm();
      checkSchoolLunchSubmission();
    };
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        checkIncompleteForm();
        checkSchoolLunchSubmission();
      }
    };
    
    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Listen for progress updates from forms
    const handleProgressUpdate = () => {
      console.log('Progress update event received, refreshing incomplete forms');
      checkIncompleteForm();
      checkSchoolLunchSubmission();
    };

    window.addEventListener('progressUpdated', handleProgressUpdate);

    // Also check periodically when component is mounted
    const interval = setInterval(() => {
      checkIncompleteForm();
      checkSchoolLunchSubmission();
    }, 10000); // Check every 10 seconds

    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('progressUpdated', handleProgressUpdate);
      clearInterval(interval);
    };
  }, []);

  const checkIncompleteForm = async () => {
    try {
      const response = await axios.get('/api/form-submissions/progress');

      // Filter for forms with actual progress
      const formsWithProgress = response.data.filter(form => (
        (!form.is_complete && form.progress_percentage > 0) ||
        (form.submission_data && Object.keys(form.submission_data).length > 0 && !form.is_complete)
      ));

      setIncompleteForms(formsWithProgress);
      try {
        const mq = await axios.get('/api/migrant-questionnaire/status');
        setMigrantOutstanding((mq.data.students || []).filter((s) => !s.submissionId));
      } catch (mqErr) {
        // Non-fatal: the dashboard still works without the nudge.
      }
    } catch (error) {
      console.error('Failed to check incomplete forms:', error);
      setIncompleteForms([]);
    }
  };

  const checkSchoolLunchSubmission = async () => {
    try {
      // Check if there's an existing school lunch submission for this user
      const response = await axios.get('/api/form-submissions/progress');
      const schoolLunchForm = response.data.find(form =>
        form.form_instance_id === null &&
        form.submission_data &&
        form.submission_data._form_type === 'school-lunch-application' &&
        form.is_complete
      );
      setSchoolLunchSubmission(schoolLunchForm || null);
    } catch (error) {
      console.error('Failed to check school lunch submission:', error);
      setSchoolLunchSubmission(null);
    }
  };

  const handleDeleteIncompleteForm = async (formId, formName) => {
    const formType = formName || 'this form';
    if (!window.confirm(`Are you sure you want to delete the incomplete ${formType}? All progress will be lost.`)) {
      return;
    }

    try {
      await axios.delete(`/api/form-submissions/progress/${formId}`);
      setIncompleteForms(prev => prev.filter(form => form.id !== formId));
    } catch (error) {
      console.error('Failed to delete incomplete form:', error);
      alert('Failed to delete form progress');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  const primaryColor = user?.primary_color || '#2c3e50';
  const secondaryColor = user?.secondary_color || '#ff9800'; // Default to orange if no secondary color
  const logoUrl = user?.logo_url;
  const [logoCandidateIndex, setLogoCandidateIndex] = useState(0);
  const logoCandidates = getDistrictLogoCandidates(user);
  const activeLogoSrc = logoCandidates[logoCandidateIndex] || getDistrictLogoSrc(user);

  useEffect(() => {
    setLogoCandidateIndex(0);
  }, [user?.logo_url, user?.logo_display_url, user?.subdomain, user?.district_name]);

  // Log logo info for debugging (can be removed in production)
  useEffect(() => {
    if (process.env.NODE_ENV === 'development' && logoUrl) {
      console.log('[Logo] Loaded:', {
        logoUrl,
        logo_display_url: user?.logo_display_url,
        resolvedSrc: getDistrictLogoSrc(user)
      });
    }
  }, [logoUrl, user]);

  // Get contrast-optimized styles for header and alert bar
  const headerStyle = getContrastStyles(primaryColor);
  // Forms the office has sent back for corrections.
  //
  // Driven by the form's own status, not by unread notifications: a notification gets marked
  // read as soon as the parent opens the bell, and the banner has to stay until the form is
  // actually fixed. Failure is silent - the bell still carries the message, and a broken
  // request here must not take the dashboard down with it.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get('/api/form-submissions/my-change-requests');
        if (!cancelled) setChangeRequests(res.data.requests || []);
      } catch (error) {
        if (!cancelled) setChangeRequests([]);
      }
    })();
    return () => { cancelled = true; };
    // Keyed on the path, not []: saving a correction navigates back inside this dashboard, so
    // with an empty dependency list the banner stayed up after the parent had already fixed
    // the form. Found by clicking Save in a real browser - the request had resolved
    // server-side while the page still told them to fix it.
  }, [location.pathname]);

  // Forms the district has said are now required and has not received yet.
  //
  // Like the send-back banner above, and keyed on the path for the same reason: submitting the
  // form navigates inside this dashboard, and the server stops returning it the moment the
  // submission lands. Read state is deliberately not involved - opening the bell must not clear
  // a banner for a form the office is still waiting on.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get('/api/notifications/required-forms');
        if (!cancelled) setRequiredForms(res.data.requests || []);
      } catch (error) {
        if (!cancelled) setRequiredForms([]);
      }
    })();
    return () => { cancelled = true; };
  }, [location.pathname]);

  const alertBarStyle = getContrastStyles(secondaryColor);
  
  const alertBarTextColor = getBestTextColor(secondaryColor);

  return (
    // parent-dashboard scopes the narrow-screen rules in ParentDashboard.css to this dashboard
    // only; dashboard-container and dashboard-content are shared with the district and
    // superadmin dashboards, whose tables are genuinely wide and want the sideways scroll.
    <div className="dashboard-container parent-dashboard">
      <header 
        className="dashboard-header" 
        style={headerStyle}
      >
        <div className="dashboard-header-content">
          <button 
            className="mobile-menu-toggle"
            onClick={toggleMobileMenu}
            aria-label="Toggle menu"
            aria-expanded={mobileMenuOpen}
          >
            <span></span>
            <span></span>
            <span></span>
          </button>
          {activeLogoSrc && (
            <img 
              src={activeLogoSrc}
              alt={user?.district_name || 'District Logo'} 
              className="dashboard-logo"
              onError={(e) => {
                const nextIndex = logoCandidateIndex + 1;
                const nextSrc = logoCandidates[nextIndex];
                if (nextSrc) {
                  setLogoCandidateIndex(nextIndex);
                } else {
                  e.target.style.display = 'none';
                }
              }}
            />
          )}
          <h1>{user?.district_name || 'District'} - Parent Portal</h1>
        </div>
        <div className="dashboard-header-actions">
          <NotificationBell onNavigate={() => {}} />
          <AccountSwitcher />
          <button onClick={handleLogout} className="btn-logout">Logout</button>
        </div>
      </header>
      {changeRequests.length > 0 && (
        <div className="incomplete-forms-alert" style={alertBarStyle}>
          <div className="incomplete-forms-alert-header">
            <span className="incomplete-forms-alert-icon">✏️</span>
            <span>
              <strong>The school needs a change:</strong>{' '}
              {changeRequests.length === 1
                ? '1 form has been sent back to you.'
                : `${changeRequests.length} forms have been sent back to you.`}
            </span>
          </div>
          <div className="incomplete-forms-alert-items">
            {changeRequests.map((r) => (
              <div key={r.form_submission_id} className="incomplete-forms-alert-item">
                <div>
                  <strong>{r.form_name}</strong>
                  {r.student_name ? ` — ${r.student_name}` : ''}
                </div>
                {r.note && <div className="change-request-note">“{r.note}”</div>}
                {r.student_id ? (
                  <Link to={`/parent/students/edit/${r.student_id}`}>Open the form</Link>
                ) : (
                  <span>Please contact the school office to make this change.</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {requiredForms.length > 0 && (
        <div className="incomplete-forms-alert" style={alertBarStyle}>
          <div className="incomplete-forms-alert-header">
            <span className="incomplete-forms-alert-icon">➕</span>
            <span className="required-forms-alert-text">
              {/* The count leads, and with a single form its name goes on the same line -
                  which is the whole banner for almost every family. Anything longer is one
                  tap away, because a card per child pushed the migrant banner off a phone
                  screen and this one sits directly above it. */}
              <strong>
                {requiredForms.length === 1
                  ? '1 form needs your attention'
                  : `${requiredForms.length} forms need your attention`}
              </strong>
              {requiredForms.length === 1 ? ` \u2014 ${requiredForms[0].form_name}` : ''}
            </span>
            {requiredForms.length > 1 && (
              <button
                type="button"
                className="incomplete-forms-alert-toggle"
                onClick={() => setRequiredAlertOpen((v) => !v)}
                aria-expanded={requiredAlertOpen}
              >
                {requiredAlertOpen ? 'Hide' : 'Show all'}
              </button>
            )}
          </div>
          <div
            className="incomplete-forms-alert-items"
            hidden={requiredForms.length > 1 && !requiredAlertOpen}
          >
            {requiredForms.map((r) => (
              <div
                key={r.id}
                className="incomplete-forms-alert-item"
                style={{
                  background: alertBarTextColor === '#ffffff' ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.1)',
                  color: alertBarTextColor
                }}
              >
                <div className="incomplete-forms-alert-item-title">
                  {r.student_name || 'Student'}
                  {requiredForms.length > 1 ? ` \u00b7 ${r.form_name}` : ''}
                </div>
                <div className="incomplete-forms-alert-item-actions">
                  {/* Nothing was sent back, so this is not a correction: the parent is being
                      asked for a form they have never filled in. Falls back the same way the
                      send-back banner does, because a child with no enrollment record has no
                      students row to link to. */}
                  {r.student_id ? (
                    <Link to={r.link} className="incomplete-forms-alert-continue">
                      Complete it →
                    </Link>
                  ) : (
                    <span>Please contact the school office.</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {migrantOutstanding.length > 0 && (
        <div className="incomplete-forms-alert" style={alertBarStyle}>
          <div className="incomplete-forms-alert-header">
            <span className="incomplete-forms-alert-icon">📋</span>
            <span>
              <strong>Migrant Education Questionnaire:</strong> the state requires this once for each
              student. {migrantOutstanding.length === 1
                ? '1 student still needs it'
                : `${migrantOutstanding.length} students still need it`}.
            </span>
            {/* One card per student pushed this banner past a full phone screen when a
                family has three or more, and it sits above a second banner doing the
                same thing. Collapsed by default; the count is the useful part and the
                links are one tap away. */}
            <button
              type="button"
              className="incomplete-forms-alert-toggle"
              onClick={() => setMigrantAlertOpen((v) => !v)}
              aria-expanded={migrantAlertOpen}
            >
              {migrantAlertOpen ? 'Hide' : 'Show all'}
            </button>
          </div>
          <div
            className="incomplete-forms-alert-items"
            hidden={!migrantAlertOpen}
          >
            {migrantOutstanding.map((s) => (
              <div
                key={s.studentIntakeId}
                className="incomplete-forms-alert-item"
                style={{
                  background: alertBarTextColor === '#ffffff' ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.1)',
                  color: alertBarTextColor
                }}
              >
                <div className="incomplete-forms-alert-item-title">{s.studentName || 'Student'}</div>
                <div className="incomplete-forms-alert-item-actions">
                  <Link
                    to={`/parent/migrant-questionnaire/${s.studentIntakeId}`}
                    className="incomplete-forms-alert-continue"
                  >
                    Complete it →
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {incompleteForms.length > 0 && (
        <div className="incomplete-forms-alert" style={alertBarStyle}>
          <div className="incomplete-forms-alert-header">
            <span className="incomplete-forms-alert-icon">⚠️</span>
            <span>
              <strong>Incomplete Forms:</strong> You have {incompleteForms.length} incomplete form{incompleteForms.length > 1 ? 's' : ''}.
            </span>
            {/* Same reason as the questionnaire banner above: each card carries a title,
                a percentage, a timestamp and two buttons, so four of them filled a phone
                screen on their own - and this banner sits directly below another one. */}
            <button
              type="button"
              className="incomplete-forms-alert-toggle"
              onClick={() => setIncompleteAlertOpen((v) => !v)}
              aria-expanded={incompleteAlertOpen}
            >
              {incompleteAlertOpen ? 'Hide' : 'Show all'}
            </button>
          </div>
          <div className="incomplete-forms-alert-items" hidden={!incompleteAlertOpen}>
            {incompleteForms.map(form => (
              <div 
                key={form.id} 
                className="incomplete-forms-alert-item"
                style={{
                  background: alertBarTextColor === '#ffffff' 
                    ? 'rgba(255,255,255,0.15)' 
                    : 'rgba(0,0,0,0.1)',
                  color: alertBarTextColor
                }}
              >
                <div className="incomplete-forms-alert-item-title">
                  {form.student_first_name
                    ? `${form.submission_data?._form_type === 'returning_student' ? 'Returning Student' : 'New Student'} - ${form.student_first_name}`
                    : (form.instance_name || form.master_form_name || 'Student Enrollment Form')
                  }
                </div>
                <div className="incomplete-forms-alert-item-meta">
                  {form.progress_percentage}% complete
                  {form.updated_at && (
                    <span>
                      {' '}• Last saved: {new Date(form.updated_at).toLocaleString()}
                    </span>
                  )}
                </div>
                <div className="incomplete-forms-alert-item-actions">
                  <Link
                    to={form.form_instance_id
                      ? `/parent/school-lunch/${form.form_instance_id}`
                      : form.submission_data?._form_type === 'returning_student'
                        ? (form.student_intake_id ? `/parent/returning-student-enrollment/${form.student_intake_id}` : '/parent/returning-student-enrollment')
                        : (form.student_intake_id ? `/parent/new-student/${form.student_intake_id}` : '/parent/new-student')}
                    className="incomplete-forms-alert-item-button incomplete-forms-alert-item-button-continue"
                    style={{
                      background: alertBarTextColor === '#ffffff' ? 'white' : '#f5f5f5',
                      color: secondaryColor
                    }}
                  >
                    Continue →
                  </Link>
                  <button
                    onClick={() => handleDeleteIncompleteForm(form.id, form.instance_name || form.master_form_name)}
                    className="incomplete-forms-alert-item-button incomplete-forms-alert-item-button-delete"
                    data-text-color={alertBarTextColor === '#ffffff' ? 'light' : 'dark'}
                    style={{
                      color: alertBarTextColor,
                      borderColor: alertBarTextColor
                    }}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {mobileMenuOpen && (
        <div className="mobile-menu-backdrop" onClick={closeMobileMenu}></div>
      )}
      <div className="dashboard-content">
        <nav className={`dashboard-sidebar ${mobileMenuOpen ? 'mobile-open' : ''}`}>
          <div className="sidebar-header">
            <h2>Menu</h2>
            <button 
              className="mobile-menu-close"
              onClick={closeMobileMenu}
              aria-label="Close menu"
            >
              ×
            </button>
          </div>
          <ul>
            <li>
              <Link to="/parent/getting-started" onClick={closeMobileMenu}>Getting Started</Link>
            </li>
            <li>
              <Link to="/parent/household-information" onClick={closeMobileMenu}>Household Information</Link>
            </li>
            <li>
              <Link to="/parent/forms" onClick={closeMobileMenu}>View Forms</Link>
            </li>
            <li>
              <Link to="/parent/student-intake" onClick={closeMobileMenu}>My Students</Link>
              <ul className="submenu">
                <li>
                  <Link to="/parent/student-intake/new" onClick={closeMobileMenu}>Add a New Student</Link>
                </li>
                <li><Link to="/parent/student-intake" onClick={closeMobileMenu}>Update Students</Link></li>
              </ul>
            </li>
            <li>
              <Link to="/parent/recently-deleted" onClick={closeMobileMenu}>Recently Deleted</Link>
            </li>
            {incompleteForms.filter(f => f.form_instance_id).map(form => (
              <li key={form.id}>
                <Link
                  to={`/parent/school-lunch/${form.form_instance_id}`}
                  onClick={closeMobileMenu}
                  className="sidebar-incomplete-form-link"
                  style={{ color: secondaryColor }}
                >
                  <span>⚠️</span>
                  <span>Continue {form.instance_name || form.master_form_name} ({form.progress_percentage}%)</span>
                </Link>
              </li>
            ))}
          </ul>
        </nav>
        <main className="dashboard-main">
          <Routes>
            <Route path="/getting-started" element={<GettingStarted />} />
            <Route path="/" element={
              <div>
                <h2>Welcome to {user?.district_name || 'District'} Parent Portal</h2>
                <p>Use the menu to add a new student or manage existing students.</p>
                
                {incompleteForms.length > 0 && (
                  <div className="welcome-incomplete-forms">
                    <h3 className="welcome-incomplete-forms-title">
                      Incomplete Forms Found
                    </h3>
                    <div className="welcome-incomplete-forms-grid">
                      {incompleteForms.map(form => (
                        <div key={form.id} className="welcome-incomplete-forms-card">
                          <h4 className="welcome-incomplete-forms-card-title">
                            {form.instance_name || form.master_form_name || 'New Student Form'}
                          </h4>
                          <p className="welcome-incomplete-forms-card-meta">
                            {form.progress_percentage}% complete
                            {form.updated_at && (
                              <span> • Last saved: {new Date(form.updated_at).toLocaleString()}</span>
                            )}
                          </p>
                          <div className="welcome-incomplete-forms-card-actions">
                            <Link
                              to={form.form_instance_id
                                ? `/parent/school-lunch/${form.form_instance_id}`
                                : form.submission_data?._form_type === 'returning_student'
                                  ? (form.student_intake_id ? `/parent/returning-student-enrollment/${form.student_intake_id}` : '/parent/returning-student-enrollment')
                                  : (form.student_intake_id ? `/parent/new-student/${form.student_intake_id}` : '/parent/new-student')}
                              className="btn-primary"
                            >
                              Continue Form
                            </Link>
                            <button
                              onClick={() => handleDeleteIncompleteForm(form.id, form.instance_name || form.master_form_name)}
                              className="btn-danger"
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* School Lunch Application Status */}
                <div style={{
                  marginTop: '2rem',
                  padding: '1.5rem',
                  background: '#f8f9fa',
                  border: '1px solid #dee2e6',
                  borderRadius: '8px'
                }}>
                  <h3 style={{ margin: '0 0 1rem 0', color: '#495057' }}>
                    School Lunch Application
                  </h3>
                  {schoolLunchSubmission ? (
                    <div style={{
                      padding: '1rem',
                      background: '#d4edda',
                      border: '1px solid #c3e6cb',
                      borderRadius: '4px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}>
                      <div>
                        <strong style={{ color: '#155724' }}>✓ Application Submitted</strong>
                        <p style={{ margin: '0.5rem 0 0 0', color: '#155724' }}>
                          Submitted on {new Date(schoolLunchSubmission.submitted_at).toLocaleDateString()}
                        </p>
                      </div>
                      <button
                        onClick={async () => {
                          try {
                            const response = await axios.post('/api/forms/school-lunch-application/generate-pdf',
                              { submissionId: schoolLunchSubmission.id },
                              { responseType: 'blob' }
                            );
                            const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
                            window.open(url, '_blank');
                          } catch (error) {
                            alert('Failed to generate PDF: ' + (error.response?.data?.error || error.message));
                          }
                        }}
                        style={{
                          background: '#28a745',
                          color: 'white',
                          border: 'none',
                          padding: '0.5rem 1rem',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontWeight: 'bold'
                        }}
                      >
                        View PDF
                      </button>
                    </div>
                  ) : (
                    <div style={{
                      padding: '1rem',
                      background: '#fff3cd',
                      border: '1px solid #ffeaa7',
                      borderRadius: '4px'
                    }}>
                      <p style={{ margin: '0 0 1rem 0', color: '#856404' }}>
                        Apply for free or reduced price school meals for your family.
                      </p>
                      <Link
                        to="/parent/school-lunch/school-lunch-application"
                        style={{
                          background: '#ffc107',
                          color: '#212529',
                          padding: '0.5rem 1rem',
                          borderRadius: '4px',
                          textDecoration: 'none',
                          fontWeight: 'bold',
                          display: 'inline-block'
                        }}
                      >
                        Start Application
                      </Link>
                    </div>
                  )}
                </div>
              </div>
            } />
            <Route path="/forms" element={<ParentFormsDashboard />} />
            <Route path="/household-information" element={<HouseholdInformation />} />
            <Route path="/recently-deleted" element={<RecentlyDeleted />} />
            <Route path="/school-lunch/:formInstanceId" element={
              <div style={{ padding: '20px' }}>
                <h1>School Lunch Application</h1>
                <p>Complete this form to apply for free or reduced price school meals. The form will generate a PDF that matches the official Missouri School Lunch Application format.</p>
                <SchoolLunchApplicationForm />
              </div>
            } />
            <Route path="/students" element={<MyStudentsList />} />
            <Route path="/student-intake" element={<StudentIntakeList />} />
            <Route path="/student-intake/new" element={<StudentIntakeForm />} />
            <Route path="/student-intake/:id/edit" element={<StudentIntakeForm />} />
            <Route path="/migrant-questionnaire/:studentIntakeId" element={<MigrantQuestionnaireRoute />} />
            <Route path="/new-student/:studentIntakeId" element={<NewStudentForm />} />
            <Route path="/new-student" element={<NewStudentForm />} />
            <Route path="/edit-student/:studentIntakeId" element={<EditStudentForm />} />
            <Route path="/returning-student-enrollment/:studentIntakeId" element={<ReturningStudentEnrollmentForm />} />
            <Route path="/returning-student-enrollment" element={<ReturningStudentEnrollmentForm />} />
            <Route path="/returning-student" element={<ReturningStudentForm />} />
            <Route path="/students/edit/:id" element={<EditStudent />} />
            <Route path="/returning-student" element={<ReturningStudentList />} />
            <Route path="/returning-student/:studentId" element={<ReturningStudentForm />} />
          </Routes>
        </main>
      </div>
    </div>
  );
};

export default ParentDashboard;

